import { Extension, type Editor } from "@tiptap/core";
import type { Transaction } from "@tiptap/pm/state";

type EditorWithTrackedChangesStorage = Editor & {
  storage: Editor["storage"] & {
    trackedChanges?: {
      enabled?: boolean;
    };
  };
};

const TRACKED_CHANGES_META_KEY = "trackedChanges";
const TRACKED_CHANGES_Y_SYNC_META_KEY = "y-sync$";

type ShouldNormalizeTrackedChangesInputTransactionParams = {
  editor: Editor;
  transaction: Transaction;
};

export const getYSyncTransactionMeta = (transaction: Transaction) => {
  const transactionMeta = (
    transaction as unknown as { meta?: Record<string, unknown> }
  ).meta;

  return Object.entries(transactionMeta ?? {}).find(([key]) =>
    key.startsWith(TRACKED_CHANGES_Y_SYNC_META_KEY)
  )?.[1];
};

export const normalizeYSyncMetaForTrackedChanges = (
  transaction: Transaction
) => {
  const ySyncMeta = getYSyncTransactionMeta(transaction);

  if (
    ySyncMeta !== undefined &&
    transaction.getMeta(TRACKED_CHANGES_Y_SYNC_META_KEY) === undefined
  ) {
    transaction.setMeta(TRACKED_CHANGES_Y_SYNC_META_KEY, ySyncMeta);
  }
};

export const shouldNormalizeTrackedChangesInputTransaction = ({
  editor,
  transaction,
}: ShouldNormalizeTrackedChangesInputTransactionParams) => {
  if (
    !transaction.docChanged ||
    transaction.getMeta("uiEvent") !== "input" ||
    transaction.getMeta("paste") !== undefined ||
    getYSyncTransactionMeta(transaction) !== undefined ||
    transaction.getMeta("addToHistory") === false ||
    transaction.getMeta(TRACKED_CHANGES_META_KEY)
  ) {
    return false;
  }

  return Boolean(
    (editor as EditorWithTrackedChangesStorage).storage.trackedChanges?.enabled
  );
};

export const TrackedChangesInputCompatibility = Extension.create({
  name: "trackedChangesInputCompatibility",

  priority: 101,

  dispatchTransaction({ transaction, next }) {
    normalizeYSyncMetaForTrackedChanges(transaction);

    if (
      shouldNormalizeTrackedChangesInputTransaction({
        editor: this.editor,
        transaction,
      })
    ) {
      transaction.setMeta("uiEvent", null);
    }

    next(transaction);
  },
});
